// src/Pages/Auth/VerifyEmail.jsx
import { useEffect, useRef, useState } from "react"; // <- useRef mukaan
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import { api } from "../../services/api";
import { toast } from "react-toastify";

export default function VerifyEmail() {
  const [search] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState("loading");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  const ranRef = useRef(false); // <- StrictMode guard

  useEffect(() => {
    if (ranRef.current) return;   // <- estä tuplajuoksu devissä
    ranRef.current = true;

    const token = search.get("token");
    const rawNext = search.get("next") || "/onboarding";
    const candidate = rawNext.startsWith("/") ? rawNext : "/onboarding";
    // Älä koskaan ohjaa setupiin vahvistuksesta
    const nextParam = /^\/setup(?:$|[/?#])/.test(candidate) ? "/onboarding" : candidate;

    if (!token) {
      setStatus("idle");
      return;
    }

    (async () => {
      try {
        setSubmitting(true);
        await api.auth.confirmVerifyEmail(token);
        setStatus("ok");
        toast.success("Sähköposti vahvistettu! 👍", { containerId: "global" });
        try { sessionStorage.setItem("dashboard.skipSetupOnce", "1"); } catch {}

        setTimeout(() => {
          (async () => {
            let loggedIn = false;
            try {
              const me = await api.auth.me();
              loggedIn = !!me?.user?.id;
            } catch {}
            if (!loggedIn) {
              navigate(`/kirjaudu?verified=1&next=${encodeURIComponent(nextParam)}`, { replace: true });
            } else {
              navigate(nextParam, { replace: true });
            }
          })();
        }, 1200);
      } catch (e) {
        // halutessasi voit käsitellä "already_verified" pehmeästi
        setStatus("error");
        const msg =
          e?.payload?.error === "token_expired"
            ? "Vahvistuslinkki on vanhentunut."
            : e?.payload?.error === "invalid_token"
            ? "Vahvistuslinkki on virheellinen."
            : "Vahvistus epäonnistui.";
        setServerError(msg);
        toast.error(msg, { containerId: "global" });
      } finally {
        setSubmitting(false);
      }
    })();
  }, [search, navigate]);


  return (
    <div className="min-h-screen bg-lemon/60 dark:bg-black/40">
      <div
        className={[
          "mx-auto max-w-6xl px-4 pb-12",
          "pt-24",
          "md:min-h-[calc(100vh-96px)] md:grid md:place-items-center",
        ].join(" ")}
      >
        <div className="w-full md:max-w-4xl lg:max-w-5xl mx-auto">
          <h1 className="text-2xl font-bold text-left md:text-center text-black/80 dark:text-white/90 mb-3">
            Vahvista sähköpostiosoitteesi
          </h1>

          <div
            className={[
              "grid grid-cols-1 gap-6",
              "md:grid-cols-2 md:items-start md:justify-center",
            ].join(" ")}
          >
            {/* Vasen: sisältökortti */}
            <div className="order-2 md:order-2">
              <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/60 backdrop-blur-md shadow-lg w-full p-4 sm:p-5">
                {/* Tilaviestit */}
                {status === "loading" && (
                  <div className="space-y-3">
                    <p className="text-black/80 dark:text-white/85">
                      Vahvistetaan sähköpostia…
                    </p>
                    <div className="flex items-center gap-2 text-sm opacity-80">
                      <Spinner /> Odota hetki
                    </div>
                  </div>
                )}

                {status === "ok" && (
                  <div className="space-y-2">
                    <p className="text-black/80 dark:text-white/85">
                      Valmis! Ohjataan eteenpäin…
                    </p>
                  </div>
                )}

                {(status === "error" || status === "idle") && (
                  <div className="space-y-5">
                    {serverError && (
                      <div className="rounded-md bg-red-100/70 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                        {serverError}
                      </div>
                    )}

                    <p className="text-black/80 dark:text-white/85">
                      Jos linkki on jo ehtinyt vanheta tai sähköposti katosi bittiavaruuteen, tilaa uusi vahvistuslinkki alta.
                    </p>

                    <ResendBlock
                      submittingGlobal={submitting}
                      setSubmittingGlobal={setSubmitting}
                      setServerError={setServerError}
                    />

                    <div className="pt-2 text-sm text-black/60 dark:text-white/60">
                      <span>Etkö vielä luonut tiliä? </span>
                      <Link to="/rekisteroidy" className="text-pink underline hover:opacity-80 dark:text-primary">
                        Rekisteröidy tästä
                      </Link>
                      .
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Oikea: Info/CTA */}
            <div className="order-1 md:order-1">
              <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/50 backdrop-blur p-4">
                <p className="text-black/80 dark:text-white/80 text-sm leading-relaxed md:text-center">
                  Vahvistuslinkki on matkalla sähköpostiisi. Jos postilaatikko näyttää tyhjää,
                  kurkkaa myös roskaposti- ja mainoskansiot – viestit tykkäävät piileskellä siellä 😉 Tarvitsetko apua?{" "}
                  <Link
                    to="/ukk-esiintyjille"
                    className="font-semibold underline text-pink dark:text-primary hover:opacity-90"
                  >
                    Lue UKK-sivu
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg
      className="animate-spin h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
    </svg>
  );
}

function ResendBlock({ submittingGlobal, setSubmittingGlobal, setServerError }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const disableAll = busy || submittingGlobal;

  const handleResend = async (e) => {
    e?.preventDefault?.();
    setServerError("");
    setBusy(true);
    setSubmittingGlobal?.(true);
    try {
      const payload = email.trim() ? { email: email.trim() } : {};
      await api.auth.sendVerifyEmail(payload);
      setSent(true);
      toast.info("Uusi vahvistuslinkki lähetetty (jos osoite löytyy).", { containerId: "global" });
    } catch (err) {
      const msg =
        err?.payload?.message ||
        err?.payload?.error ||
        "Uuden vahvistuslinkin lähetys epäonnistui.";
      setServerError(String(msg));
    } finally {
      setBusy(false);
      setSubmittingGlobal?.(false);
    }
  };

  const labelBase =
    "block font-customFont1 text-sm uppercase tracking-wide text-black/70 dark:text-white/80 mb-1";
  const inputBase =
    "w-full h-11 rounded-lg px-3 font-customFont1 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-pink/60 dark:focus:ring-primary text-base";

  return (
    <form onSubmit={handleResend} className="space-y-3" noValidate>
      <div>
        <label className={labelBase} htmlFor="resendEmail">
          Sähköposti (valinnainen)
        </label>
        <input
          id="resendEmail"
          type="email"
          name="email"
          placeholder="nimi@osoite.fi"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputBase}
          disabled={disableAll}
          autoComplete="email"
        />
        <p className="mt-1 text-xs text-black/50 dark:text-white/50">
          Jos olet kirjautunut, jätä tyhjäksi. Muuten syötä osoite, johon linkki lähetetään.
        </p>
      </div>

      <NeonButton
        type="submit"
        variant="primary"
        size="md"
        className="w-full flex items-center justify-center gap-2"
        disabled={disableAll}
        aria-busy={disableAll ? "true" : "false"}
      >
        {disableAll && <Spinner />}
        {sent ? "Lähetetty!" : "Lähetä vahvistuslinkki uudelleen"}
      </NeonButton>
    </form>
  );
}
